import type { Citation } from '@ctw/shared/api/ai/types';
import { getAllergyIntolerancesById } from '@ctw/shared/api/fhir/allergies';
import { useBinaryId } from '@ctw/shared/api/fhir/binaries';
import { fetchConditionsByIdFQS } from '@ctw/shared/api/fhir/conditions';
import { getDocumentReferenceWithBasicsById } from '@ctw/shared/api/fhir/document';
import { getMedicationStatementsByIdFQS } from '@ctw/shared/api/fhir/medications';
import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { ProcedureModel } from '@ctw/shared/api/fhir/models/procedure';
import { fetchObservationsById } from '@ctw/shared/api/fhir/observations';
import { fetchProceduresById } from '@ctw/shared/api/fhir/procedure';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { CitationComponent } from './citation';

import type { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { getBinaryById } from '@ctw/shared/api/fqs-rest/binaries';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { useEffect, useMemo, useState } from 'react';

export type SupportedCitationLinkResourceType =
  | Array<AllergyModel>
  | Array<ConditionModel>
  | Array<DocumentModel>
  | Array<MedicationStatementModel>
  | Array<ObservationModel>
  | Array<ProcedureModel>
  | null;

export type CitationLinkProps = {
  resourceInfo: { resourceID: string; resourceType: string };
  citation: Citation;
};

export const CitationLink = ({ resourceInfo, citation }: CitationLinkProps) => {
  const telemetry = useTelemetry();
  const { ctwFetch, requestContext } = useCTW();
  const { isLoading, isError, data } = usePatientQuery<SupportedCitationLinkResourceType>({
    queryId: 'ResourceByTypeAndIDQuery',
    queryKey: [resourceInfo.resourceID, resourceInfo.resourceType],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      switch (resourceInfo.resourceType) {
        case 'AllergyIntolerance':
          return await getAllergyIntolerancesById(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Condition':
          return await fetchConditionsByIdFQS(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'DocumentReference':
          return await getDocumentReferenceWithBasicsById(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'MedicationStatement':
          return await getMedicationStatementsByIdFQS(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Observation':
          return await fetchObservationsById(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Procedure':
          return await fetchProceduresById(telemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        default:
          return Promise.resolve(null);
      }
    },
  });

  const binaryIdQuery = useBinaryId(data?.[0], !isLoading, true);
  const binaryId = useMemo(() => binaryIdQuery.data, [binaryIdQuery.data]);
  const [binary, setBinary] = useState<BinaryModel | null>(null);
  const { openResourceModal } = useResourceModal();
  const { openResourceDrawer } = useResourceDrawer();

  useEffect(() => {
    if (data?.[0]?.resourceType !== 'DocumentReference' && binaryId) {
      void getBinaryById({ binaryId, ctwFetch, requestContext }).then((binary) => {
        setBinary(binary ?? null);
      });
    }
  }, [data, binaryId, ctwFetch, requestContext]);

  if (isLoading || isError || !data) {
    return null;
  }

  // if it's not an array, it's not a valid resource
  if (!Array.isArray(data)) {
    return null;
  }

  if (data.length === 0) {
    return null;
  }

  const resource = data[0];

  const trackCitationLinkClick = () => {
    telemetry.trackInteraction('citation_link_click', {
      citationNumber: citation.id,
    });
  };

  switch (resource.resourceType) {
    case 'AllergyIntolerance': {
      const allergy = resource as AllergyModel;

      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();

              if (binary) {
                openResourceModal({ telemetry, model: binary });
              }
            }
            return openResourceDrawer({ telemetry, model: allergy });
          }}
        />
      );
    }
    case 'Condition': {
      const condition = resource as ConditionModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryIdQuery.data) {
              trackCitationLinkClick();

              if (binary) {
                openResourceModal({ telemetry, model: binary });
              }
            }
            return openResourceDrawer({ telemetry, model: condition });
          }}
        />
      );
    }
    case 'DocumentReference': {
      const document = resource as DocumentModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (document.binaryId) {
              trackCitationLinkClick();
              return openResourceModal({
                telemetry,
                model: document,
              });
            }
            return openResourceDrawer({ telemetry, model: document });
          }}
        />
      );
    }
    case 'MedicationStatement': {
      const medication = resource as MedicationStatementModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryIdQuery.data) {
              trackCitationLinkClick();

              if (binary) {
                openResourceModal({ telemetry, model: binary });
              }
            }
            return openResourceDrawer({ telemetry, model: medication });
          }}
        />
      );
    }
    case 'Observation': {
      const observation = resource as ObservationModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryIdQuery.data) {
              trackCitationLinkClick();

              if (binary) {
                openResourceModal({ telemetry, model: binary });
              }
            }
            return openResourceDrawer({ telemetry, model: observation });
          }}
        />
      );
    }
    case 'Procedure': {
      const procedure = resource as ProcedureModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (procedure.binaryId) {
              trackCitationLinkClick();

              if (binary) {
                openResourceModal({ telemetry, model: binary });
              }
            }
            // TODO: open procedure drawer
            return null;
          }}
        />
      );
    }
    default:
      return null;
  }
};
